import React, { useCallback, useMemo, useState } from 'react'
import { getSystemParametersSelector } from '_/modules/service-orders/selectors'
import { useSelector } from 'react-redux'
import { KANBAN_ACTIONS_SYSTEM_PARAM_NAME } from '../constants'
import { MANAGER_MAPPER } from '../managers'
import { Loader } from '@refera/ui-web'
import { useToast } from '_/hooks/use-toast'
import { useDialog } from '_/hooks/use-dialog'

/**
 *
 * @param {Object} config - The parameters for the action manager.
 * @param {Function} config.moveCardFn - The function to move the card. Default is an empty function.
 * @returns
 */
export const useKanbanServiceOrderActions = ({ moveCardFn } = { moveCardFn: () => {} }) => {
  const systemParameters = useSelector(getSystemParametersSelector)
  const { showToast } = useToast()
  const { showDialog, closeDialog, stackDialog } = useDialog()
  const kanbanColumnActionsActive = useMemo(() => {
    return (
      systemParameters?.find(param => param.name === KANBAN_ACTIONS_SYSTEM_PARAM_NAME)?.isActive ||
      false
    )
  }, [systemParameters])
  const [ManagerComponent, setManagerComponent] = useState(null)

  /**
   * Ends the current action manager.
   * @param {Object} params - The parameters for ending the action manager.
   * @param {'success' | 'error' | 'unfinished'} params.reason - The reason for ending the action manager. Default is 'success'.
   */
  const endActionFn = useCallback(
    ({ reason, message } = { reason: undefined, message: undefined }) => {
      switch (reason) {
        case 'success':
          moveCardFn()
          showToast({
            message,
            type: 'success',
          })
          break
        case 'error':
          showToast({
            message: message || 'Ocorreu um problema ao tentar realizar a ação',
            type: 'error',
          })
          break
        case 'unfinished':
          showDialog({
            type: 'warning',
            subject: 'Atenção',
            description:
              message ||
              'A ação não foi finalizada e o card não se encontrará na coluna destino. Deseja continuar?',
            labelApprove: 'Sim',
            labelCancel: 'Não',
            onApprove: () => {
              closeDialog()
              setManagerComponent(null)
            },
            onCancel: () => {
              closeDialog()
            },
          })
          return
        default:
          break
      }
      setManagerComponent(null)
    },
    [closeDialog, stackDialog, moveCardFn, showDialog, showToast]
  )

  const setActionsManager = useCallback(
    ({ from, to, cardId }) => {
      if (!kanbanColumnActionsActive || from === to) {
        endActionFn()
        return
      }

      const loader = MANAGER_MAPPER[from]?.[to]

      // Not supposed to happen, but just in case
      if (!loader) {
        endActionFn({ reason: 'error' })
        return
      }

      const serviceOrderId = cardId.split('-')[1]
      const CurrentManagerComponent = React.lazy(loader)

      setManagerComponent(
        <React.Suspense fallback={<Loader hasBackdrop open />}>
          <CurrentManagerComponent serviceOrderId={serviceOrderId} endActionFn={endActionFn} />
        </React.Suspense>
      )
    },
    [kanbanColumnActionsActive, endActionFn]
  )

  return { setActionsManager, ManagerComponent }
}
